import { motion } from 'motion/react';
import { memo } from 'react';
import { SearchButton } from '@/features/GlobalSearch/components/search/SearchButton';
import { SearchResultsArtist } from '@/features/GlobalSearch/components/search/SearchResultsArtist';
import { PopularSearchCarousel } from '@/features/GlobalSearch/components/shared/PopularSearchCarousel';
import { SearchCarousel } from '@/features/GlobalSearch/components/shared/SearchCarousel';
import { SectionHeading } from '@/features/GlobalSearch/components/trending/SectionHeading';
import { getTrendingResultUrl } from '@/features/GlobalSearch/utils/getTrendingSearches';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponseHit } from '@music-vine/setlist';
interface MusicTrendingSectionProps {
  closePopup: () => void;
  onNameClick: (content: string | undefined, endSlug: string | undefined, assetType: AssetType | 'general') => void;
  trackTrendingClick: (endSlug: string) => void;
  trendingArtists?: {
    hits?: SearchResponseHit<GenericSearchResult>[];
  };
  trendingMusicSearches?: {
    hits?: SearchResponseHit<GenericSearchResult>[];
  };
  trendingPlaylists?: {
    hits?: SearchResponseHit<GenericSearchResult>[];
  };
}
export const MusicTrendingSection = memo(({
  trendingMusicSearches,
  trendingPlaylists,
  trendingArtists,
  closePopup,
  trackTrendingClick,
  onNameClick
}: MusicTrendingSectionProps) => {
  const {
    isMd
  } = useTWScreens();
  const hasPlaylists = trendingPlaylists?.hits && trendingPlaylists.hits.length > 0;
  const hasArtists = trendingArtists?.hits && trendingArtists.hits.length > 0;

  // Use motion.div only on desktop, regular div on mobile to avoid iOS Safari touch issues
  const AnimatedContainer = isMd ? motion.div : 'div';
  const AnimatedItem = isMd ? motion.div : 'div';
  return <AnimatedContainer animate={isMd ? {
    opacity: 1
  } : undefined} initial={isMd ? {
    opacity: 0
  } : undefined} style={{
    position: 'relative'
  }} transition={isMd ? {
    duration: 0.25
  } : undefined}>
        <div className="main-search-results my-4 px-4 md:my-0 md:mb-5 md:px-0">
          <div className="mb-2">
            <SectionHeading>Popular searches</SectionHeading>
          </div>

          {/* Desktop view */}
          <div className="hidden max-h-6 overflow-hidden md:flex md:flex-wrap md:gap-2 focus-within:[&:has(:focus-visible)]:max-h-none focus-within:[&:has(:focus-visible)]:overflow-visible">
            {trendingMusicSearches?.hits?.slice(0, 6).map(hit => {
          const {
            document
          } = hit;
          const href = getTrendingResultUrl({
            name: document.name ?? '',
            type: 'music'
          });
          return <AnimatedItem animate={isMd ? {
            opacity: 1,
            scale: 1
          } : undefined} initial={isMd ? {
            opacity: 0,
            scale: 0.95
          } : undefined} key={document.id} transition={isMd ? {
            duration: 0.2,
            delay: 0
          } : undefined}>
                  <SearchButton assetType="music" closePopup={closePopup} href={href} id={document.id} name={document.name ?? ''} onNameClick={onNameClick} testId="popular-music-search" />
                </AnimatedItem>;
        })}
          </div>

          {/* Mobile Carousel */}
          <div className="-mx-4 block md:hidden">
            <PopularSearchCarousel assetType="music" closePopup={closePopup} hits={trendingMusicSearches?.hits} onNameClick={onNameClick} testId="popular-music-search" />
          </div>
        </div>

        {hasPlaylists && <div className="main-search-results my-4 px-4 md:my-0 md:mb-5 md:px-0">
            <div className="mb-2">
              <SectionHeading>Playlists</SectionHeading>
            </div>
            <SearchCarousel assetType="music" closePopup={closePopup} data={trendingPlaylists.hits} onTileClick={trackTrendingClick} />
          </div>}

        {hasArtists && <div className="main-search-results my-4 px-4 md:my-0 md:mb-0 md:px-0">
            <div className="mb-2">
              <SectionHeading>Artists</SectionHeading>
            </div>
            <AnimatedItem className="flex flex-col gap-4">
              {trendingArtists.hits!.slice(0, 8).map((hit, index) => <AnimatedItem animate={isMd ? {
          opacity: 1,
          y: 0
        } : undefined} className={cn(index >= 3 && 'hidden md:block')} initial={isMd ? {
          opacity: 0,
          y: 10
        } : undefined} key={hit.document.id} transition={isMd ? {
          duration: 0.3,
          delay: 0
        } : undefined}>
                  <SearchResultsArtist closePopup={closePopup} item={hit.document} searchType="TRENDING" />
                </AnimatedItem>)}
            </AnimatedItem>
          </div>}
      </AnimatedContainer>;
});
MusicTrendingSection.displayName = 'MusicTrendingSection';